/**
 * StaffManagementPage
 *
 * Hub screen for the Staff Management feature.
 * Gated behind the "Pallet Workforce" add-on via <AddonGate>.
 * Shows navigation tiles to Capture, Enrollment (HR_ADMIN only), and History.
 */

import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import AddonGate from "../../../../components/AddonGate";
import { UserContext } from "../../../../App";
import "./StaffManagementPage.css";

/* ── Tile icons ──────────────────────────────────────── */
function CaptureIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function EnrollIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      <path d="M21 15l2 2 4-4" transform="translate(-6 0) scale(0.75)" />
    </svg>
  );
}

function HistoryIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

/* ── Hub tiles config ────────────────────────────────── */
const TILES = [
  {
    id: "capture",
    path: "/products/staff-management/capture",
    title: "Attendance Capture",
    desc: "Face recognition clock-in and clock-out for staff.",
    icon: <CaptureIcon />,
    color: "#1e5fbc",
    requiresRole: null,
  },
  {
    id: "enrollment",
    path: "/products/staff-management/enrollment",
    title: "Employee Enrollment",
    desc: "Register an employee's face for recognition.",
    icon: <EnrollIcon />,
    color: "#0d6eaf",
    requiresRole: "HRMS_ADMIN",
  },
  {
    id: "history",
    path: "/products/staff-management/history",
    title: "Attendance History",
    desc: "Search and review attendance records by date range.",
    icon: <HistoryIcon />,
    color: "#0d7c66",
    requiresRole: null,
  },
];

/* ── Lock icon (shown on restricted tiles for non-admins) ─ */
function LockIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

/* ── Screen content ──────────────────────────────────── */
function StaffManagementContent() {
  const navigate = useNavigate();
  const { isDarkMode } = useContext(UserContext) || {};
  const [accessError, setAccessError] = React.useState(null);

  const userRoles = (() => {
    try {
      const parsed = JSON.parse(localStorage.getItem("user_roles") || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  })();
  const isHrAdmin = Array.isArray(userRoles) && userRoles.includes("HRMS_ADMIN");

  function handleTileClick(tile) {
    if (tile.requiresRole === "HRMS_ADMIN" && !isHrAdmin) {
      setAccessError(
        "Employee Enrollment is restricted to HR Administrators. " +
          "Contact your manager or HR Admin to get access."
      );
      return;
    }
    setAccessError(null);
    navigate(tile.path);
  }

  return (
    <div className={`smp-root${isDarkMode ? " dark" : ""}`}>
      <div className="smp-header">
        <div className="smp-header-content">
          <div className="smp-header-left">
            <button
              className="smp-back-btn"
              onClick={() => navigate("/products/operations-menu")}
              type="button"
              aria-label="Back to Operations Menu"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-arrow-left w-5 h-5 text-slate-600"
                aria-hidden="true"
              >
                <path d="m12 19-7-7 7-7"></path>
                <path d="M19 12H5"></path>
              </svg>
            </button>
            <div className="smp-header-text">
              <h1>Staff Management</h1>
              <p>Pallet Workforce add-on</p>
            </div>
          </div>
        </div>
      </div>

      {/* Hub tiles */}
      <div className="smp-body">
        {/* Access-denied banner */}
        {accessError && (
          <div className="smp-access-error" role="alert">
            <span className="smp-access-error-icon" aria-hidden="true">
              🔒
            </span>
            <span className="smp-access-error-text">{accessError}</span>
            <button
              className="smp-access-error-close"
              onClick={() => setAccessError(null)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        <div className="smp-tiles">
          {TILES.map((tile) => {
            const locked = tile.requiresRole === "HR_ADMIN" && !isHrAdmin;
            return (
              <button
                key={tile.id}
                className={`smp-tile${locked ? " smp-tile--locked" : ""}`}
                onClick={() => handleTileClick(tile)}
                aria-label={tile.title}
              >
                <div className="smp-tile-icon" style={{ "--tile-color": tile.color }}>
                  {tile.icon}
                </div>
                <div className="smp-tile-content">
                  <div className="smp-tile-title-row">
                    <span className="smp-tile-title">{tile.title}</span>
                    {locked && (
                      <span className="smp-tile-lock-badge" title="HR Admin only">
                        <LockIcon />
                      </span>
                    )}
                  </div>
                  <span className="smp-tile-desc">{tile.desc}</span>
                  {locked && <span className="smp-tile-role-hint">Requires HR Admin role</span>}
                </div>
                <div className="smp-tile-arrow">
                  <ChevronRight />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── Default export — wrapped in AddonGate ────────────── */
export default function StaffManagementPage() {
  return (
    <AddonGate addonName="pallet workforce">
      <StaffManagementContent />
    </AddonGate>
  );
}
